interface Props {
  /** ISO 3166-1 alpha-2 country code, for example `se`. Casing does not matter. */
  country: string | null | undefined
}

const regionNames = new Intl.DisplayNames(['en'], {type: 'region'})

/** Resolve `se` to `Sweden`, falling back to the bare code for anything unrecognised. */
const countryName = (code: string) => {
  try {
    const name = regionNames.of(code)
    return name && name !== code ? name : code
  } catch {
    return code
  }
}

/**
 * Renders a country's flag as an emoji, or nothing at all when the code is missing
 * or not a recognisable alpha-2 code. The full country name is exposed as the tooltip
 * and the accessible label, which is what makes the less familiar codes readable.
 *
 * The glyphs come from the `Twemoji Country Flags` font declared in `index.scss`,
 * because Windows ships no flag glyphs of its own. The font is applied with an inline
 * style rather than a class on purpose: several cards and lists style their values with
 * a nested `'& p': {'& span': {fontFamily: ...}}` rule, which is more specific than any
 * single class and would otherwise win here. That leaves the flag on a font with no flag
 * glyph.
 */
const CountryFlag = ({country}: Props) => {
  if (!country || !/^[a-z]{2}$/i.test(country)) return null

  const code = country.toUpperCase()

  // An alpha-2 code maps to its flag by shifting each letter into the Regional
  // Indicator Symbol block, which starts 127397 code points above 'A'.
  const flag = code
    .split('')
    .map(character => String.fromCodePoint(character.charCodeAt(0) + 127397))
    .join('')

  return (
    <span
      style={{
        fontFamily: '"Twemoji Country Flags", sans-serif',
        fontSize: '1.15em',
        lineHeight: 1
      }}
      role="img"
      aria-label={countryName(code)}
      title={countryName(code)}>
      {flag}
    </span>
  )
}

export default CountryFlag
